import moment from 'moment';
import { CheckActiveOverdraftAccountDetails } from '../domain/pismo.domain';
import { LoanPurposeValue } from '../domain/component/loan-purpose-selection-component.domain';

/**
 * Result of business overdraft fee calculation
 */
export interface BusinessOverdraftFeeCalculationResult {
  earlyTerminationFee?: number;
  earlyTerminationFeePercent?: number;
  brokerageDollar?: number;
  brokerage?: number;
}

/**
 * Loan purposes that trigger the business overdraft variation fee rules (delta / $0 / full-limit
 * plus early termination fee) instead of charging brokerage on the full facility limit.
 */
export const BUSINESS_OVERDRAFT_VARIATION_PURPOSES = ['Limit Increase', 'Limit Decrease', 'Renewal'] as const;

/** Single source of truth for "is this a BO variation purpose?" — avoids duplicating the name list. */
export function isBusinessOverdraftVariationPurpose(loanPurpose: LoanPurposeValue | null | undefined): boolean {
  return !!loanPurpose && (BUSINESS_OVERDRAFT_VARIATION_PURPOSES as readonly string[]).includes(loanPurpose.name);
}

/**
 * @internal Use `calculateBusinessOverdraftFees` from `app-calculator.ts` instead.
 * This function is kept exported only for direct unit testing.
 * 
 * Calculate fees for business overdraft variations (Limit Increase, Limit Decrease, Renewal)
 * 
 * Fee rules:
 * - Limit Increase:
 *   - With Renewal: Brokerage on FULL requested limit + early termination fee (1.5% on current limit if expiry > 90 days)
 *   - Without Renewal: Brokerage on increased amount only (newLimit - oldLimit)
 * - Limit Decrease:
 *   - With Renewal: Brokerage on FULL requested limit + early termination fee (1.5% on current limit if expiry > 90 days)
 *   - Without Renewal: brokerage = $0, 1.5% fee on reduced amount
 * - Renewal only: Brokerage on FULL requested limit + early termination fee (1.5% on current limit if expiry > 90 days)
 * 
 * @param activeAccountDetails - Details of the active overdraft account (null = skip variation logic)
 * @param loanPurpose - The loan purpose (Limit Increase, Limit Decrease, or Renewal)
 * @param termRenewal - Whether term renewal is selected
 * @param requestedLimit - The requested limit amount
 * @param brokeragePercent - The brokerage percentage to apply
 * @returns Fee calculation result with early termination fee and/or brokerage
 */
export function calculateBusinessOverdraftVariationFees(
  activeAccountDetails: CheckActiveOverdraftAccountDetails | null,
  loanPurpose: LoanPurposeValue | null,
  termRenewal: boolean,
  requestedLimit: number,
  brokeragePercent: number
): BusinessOverdraftFeeCalculationResult {
  
  let earlyTerminationFee: number | undefined;
  let earlyTerminationFeePercent: number | undefined;
  let brokerageDollar: number | undefined;
  let brokerage: number | undefined = brokeragePercent;

  if (!activeAccountDetails || !loanPurpose) {
    return { earlyTerminationFee, earlyTerminationFeePercent, brokerageDollar, brokerage };
  }

  const currentLimit = activeAccountDetails.currentLimit ?? 0;
  const accountExpiryDate = activeAccountDetails.accountExpiryDate;
  const daysUntilExpiry = accountExpiryDate ? moment(accountExpiryDate).diff(moment(), 'days') : 0;
  const isExpiryMoreThan90Days = daysUntilExpiry > 90;

  const EARLY_TERMINATION_FEE_PERCENT = 1.5;

  switch (loanPurpose.name) {
    case 'Limit Increase':
      if (termRenewal) {
        // Limit Increase + Renewal: Calculate brokerage on FULL requested limit
        brokerageDollar = ((brokeragePercent ?? 0) / 100) * requestedLimit;
        
        // Apply early termination fee if expiry > 90 days
        if (isExpiryMoreThan90Days) {
          earlyTerminationFeePercent = EARLY_TERMINATION_FEE_PERCENT;
          earlyTerminationFee = (EARLY_TERMINATION_FEE_PERCENT / 100) * currentLimit;
        }
      } else {
        // Limit Increase + No Renewal: Calculate brokerage on increased amount only
        const increasedAmount = requestedLimit - currentLimit;
        if (increasedAmount > 0) {
          brokerageDollar = ((brokeragePercent ?? 0) / 100) * increasedAmount;
        } else {
          // If no increase (edge case), set to 0
          brokerageDollar = 0;
          brokerage = 0;
        }
      }
      break;

    case 'Limit Decrease':
      if (termRenewal) {
        // Limit Decrease + Renewal: Calculate brokerage on FULL requested limit
        brokerageDollar = ((brokeragePercent ?? 0) / 100) * requestedLimit;
        
        // Apply early termination fee if expiry > 90 days
        if (isExpiryMoreThan90Days) {
          earlyTerminationFeePercent = EARLY_TERMINATION_FEE_PERCENT;
          earlyTerminationFee = (EARLY_TERMINATION_FEE_PERCENT / 100) * currentLimit;
        }
      } else {
        // Limit Decrease + No Renewal: brokerage = $0, apply 1.5% fee on reduced amount
        brokerageDollar = 0;
        brokerage = 0;
        const reducedAmount = currentLimit - requestedLimit;
        if (reducedAmount > 0) {
          earlyTerminationFeePercent = EARLY_TERMINATION_FEE_PERCENT;
          earlyTerminationFee = (EARLY_TERMINATION_FEE_PERCENT / 100) * reducedAmount;
        }
      }
      break;

    case 'Renewal':
      // Renewal only: Calculate brokerage on FULL requested limit
      brokerageDollar = ((brokeragePercent ?? 0) / 100) * requestedLimit;
      
      // Apply early termination fee if expiry > 90 days
      if (isExpiryMoreThan90Days) {
        earlyTerminationFeePercent = EARLY_TERMINATION_FEE_PERCENT;
        earlyTerminationFee = (EARLY_TERMINATION_FEE_PERCENT / 100) * currentLimit;
      }
      break;

    default:
      // New application or other loan purposes - no variation fees
      break;
  }

  return { earlyTerminationFee, earlyTerminationFeePercent, brokerageDollar, brokerage };
}
